import { Injectable } from '@angular/core';
import { ModalController } from '@ionic/angular';
import { PresenceWarningModalComponent } from 'src/app/components/modals/presence-warning-modal/presence-warning-modal.component';
import { Nullable } from 'src/core/common/domain/types/types';

/**
 * Only 'timeout' and 'logout' mean the user is gone. 'closed' is a modal that someone else took down: presence
 * control itself, or the sweep of an expired session.
 */
export type PresenceModalResult = 'confirmed' | 'timeout' | 'logout' | 'closed';

@Injectable({
  providedIn: 'root',
})
export class PresenceModalService {
  private isOpen = false;
  private isCloseRequested = false;
  private modal: Nullable<HTMLIonModalElement> = null;

  constructor(private readonly modalController: ModalController) {}

  public async closeWarningModal(): Promise<void> {
    if (!this.isOpen) {
      return;
    }

    // Ionic ignores a dismiss that arrives before the modal has been presented, so openWarningModal looks at
    // this flag again once the modal is up.
    this.isCloseRequested = true;

    if (!this.modal) {
      return;
    }

    await this.modal.dismiss(
      {
        confirmed: false,
      },
      'closed'
    );
  }

  public async openWarningModal(countdownSeconds: number): Promise<PresenceModalResult> {
    if (this.isOpen) {
      return 'closed';
    }

    this.isOpen = true;
    this.isCloseRequested = false;

    const modal = await this.modalController.create({
      component: PresenceWarningModalComponent,
      componentProps: {
        countdownSeconds,
      },
      backdropDismiss: false,
      canDismiss: (data: any, role?: string) => {
        return Promise.resolve(role === 'confirm' || role === 'logout' || role === 'timeout' || role === 'closed');
      },
      cssClass: 'user-presence-warning',
    });
    this.modal = modal;

    let timeoutId: number | null = null;

    try {
      await modal.present();
      if (this.isCloseRequested) {
        await modal.dismiss(
          {
            confirmed: false,
          },
          'closed'
        );
        return 'closed';
      }

      const dismissPromise: Promise<PresenceModalResult> = modal
        .onDidDismiss()
        .then((res) => this.resultOfDismissal(res.role));

      const timeoutPromise: Promise<PresenceModalResult> = new Promise((resolve) => {
        timeoutId = window.setTimeout((): void => {
          resolve('timeout');
        }, countdownSeconds * 1000);
      });

      const result: PresenceModalResult = await Promise.race([dismissPromise, timeoutPromise]);

      if (result === 'timeout') {
        await modal.dismiss(
          {
            confirmed: false,
          },
          'timeout'
        );
      }

      return result;
    } finally {
      if (timeoutId !== null) {
        window.clearTimeout(timeoutId);
      }

      this.modal = null;
      this.isOpen = false;
    }
  }

  private resultOfDismissal(role?: string): PresenceModalResult {
    if (role === 'confirm') {
      return 'confirmed';
    }

    if (role === 'logout') {
      return 'logout';
    }

    return 'closed';
  }
}
